﻿import { AxiosInstance } from "axios";
import { RunStatusEnum } from "enums/RunStatusEnum";
import { isCancelled } from "helpers";
import { triggerDownload } from "helpers";
import { createLogger } from "logging";
import { AddRunModel } from "models/AddRunModel";
import { EntityActionResult } from "models/EntityActionResult";
import { PatchRunModel } from "models/PatchRunModel";
import { PatchRunStatusModel } from "models/PatchRunStatusModel";
import { RunModel } from "models/RunModel";

const logger = createLogger("RunApi");

export class RunApi {
  constructor(private axios: AxiosInstance) {}

  async getVoteUrl(id: string): Promise<string> {
    try {
      const response = await this.axios.get<string>(`/runs/${id}/voteurl`);
      return response.data;
    } catch (error) {
      logger.error(`Failed to fetch vote url of run ${id}`, error);
      throw error;
    }
  }

  async getRun(id: string, signal?: AbortSignal): Promise<RunModel> {
    try {
      const response = await this.axios.get<RunModel>(`/runs/${id}`, { signal });
      return response.data;
    } catch (error) {
      if (!isCancelled(error)) {
        logger.error("Failed to fetch run", error);
      }
      throw error;
    }
  }

  async changeRunStatus(runId: string, newStatus: RunStatusEnum): Promise<EntityActionResult> {
    const patch: PatchRunStatusModel = { id: runId, status: newStatus };
    try {
      const response = await this.axios.patch<EntityActionResult>(`/runs/${runId}/status`, patch);
      return response.data;
    } catch (error) {
      logger.error("Failed to change run status", error, patch);
      throw error;
    }
  }

  async sendMail(patch: PatchRunStatusModel): Promise<EntityActionResult> {
    try {
      const response = await this.axios.put<EntityActionResult>(`/runs/${patch.id}/sendMail`, patch);
      return response.data;
    } catch (error) {
      logger.error("Failed to send email", error);
      throw error;
    }
  }

  async downloadMail(patch: PatchRunStatusModel): Promise<void> {
    try {
      const response = await this.axios.put<BlobPart>(`/runs/${patch.id}/downloadMail`, patch, {
        responseType: "blob",
        withCredentials: true,
      });
      triggerDownload(response.data, "email.msg");
    } catch (error) {
      logger.error("Failed to download email", error);
      throw error;
    }
  }

  async createRun(model: AddRunModel): Promise<EntityActionResult> {
    try {
      const response = await this.axios.post<EntityActionResult>(`/runs`, model);
      return response.data;
    } catch (error) {
      logger.error("Failed to create run", error);
      throw error;
    }
  }

  async updateRun(model: PatchRunModel): Promise<EntityActionResult> {
    const id = model.id;
    if (!id) throw new Error("Patch.id is mandatory!");

    try {
      const response = await this.axios.put<EntityActionResult>(`/runs/${id}`, model);
      return response.data;
    } catch (error) {
      logger.error("Failed to update run", error);
      throw error;
    }
  }

  async deleteRun(runId: string): Promise<EntityActionResult> {
    try {
      const response = await this.axios.delete<EntityActionResult>(`/runs/${runId}`);
      return response.data;
    } catch (error) {
      logger.error("Failed to delete run", error);
      throw error;
    }
  }

  async sendRunToSiVe(runId: string): Promise<EntityActionResult> {
    try {
      const response = await this.axios.put<EntityActionResult>(`/runs/${runId}/sendToSiVe`);
      return response.data;
    } catch (error) {
      logger.error("Failed to send to SiVe", error);
      throw error;
    }
  }
}
